"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const client_library_1 = require("@seneca/client-library");
const session_aggregation_1 = require("@seneca/session-aggregation");
const utils_1 = require("./utils");
const errors_1 = require("./errors");
class StatsServiceClient extends client_library_1.Client {
    constructor(fetch, options) {
        super(fetch, options);
    }
    async emitSession(session) {
        const stringifiedBody = JSON.stringify(session);
        const unicodeNormalisedBody = stringifiedBody.normalize('NFC');
        await this.requestMaker.makeRequest(`${this.url}/api/stats/sessions`, {
            method: 'POST',
            body: unicodeNormalisedBody
        });
    }
    async fetchUserStats() {
        return this._fetchStatForUser(`${this.url}/api/stats/users`);
    }
    async fetchCourseSectionStatsForUser(courseId, options = {}) {
        return this._pageMethod({
            method: options => this.fetchPageOfAllStatsForUser(options),
            pageToItems: page => page.stats,
            options: Object.assign(Object.assign({}, options), { courseId, url: `${this.url}/api/stats/sections` })
        });
    }
    async fetchCourseContentStatsForUser(courseId, options = {}) {
        return this._pageMethod({
            method: options => this.fetchPageOfAllStatsForUser(options),
            pageToItems: page => page.stats,
            options: Object.assign(Object.assign({}, options), { courseId, url: `${this.url}/api/stats/contents` })
        });
    }
    async fetchAllCourseStatsForUser(options = {}) {
        return this._pageMethod({
            method: options => this.fetchPageOfAllStatsForUser(options),
            pageToItems: page => page.stats,
            options: Object.assign(Object.assign({}, options), { url: `${this.url}/api/stats/courses/user` })
        });
    }
    async fetchPageOfAllStatsForUser(options) {
        let url = options.url;
        const params = this._generatePagingQueryParams(options);
        if (options.courseId) {
            params.unshift(`courseId=${options.courseId}`);
        }
        if (params.length !== 0) {
            url += `?${params.join('&')}`;
        }
        return this._fetchStatForUser(url);
    }
    async adminFetchAllCourseStatsForUser(userId, options = {}) {
        return this._pageMethod({
            method: options => this.adminFetchPageOfAllCourseStatsForUser(userId, options),
            pageToItems: page => page.stats,
            options
        });
    }
    async adminFetchPageOfAllCourseStatsForUser(userId, options = {}) {
        let url = `${this.url}/api/admin/stats/user/${userId}/courses`;
        const params = this._generatePagingQueryParams(options);
        if (params.length !== 0) {
            url += `?${params.join('&')}`;
        }
        return this._fetchStatForUser(url);
    }
    async adminFetchWindowedCourseStatForUser({ courseId, userId, startEpochSeconds, endEpochSeconds }) {
        let url = `${this.url}/api/admin/stats/user/${userId}/windowed/course/${courseId}`;
        const params = this._generateWindowQueryParams({
            startTime: startEpochSeconds,
            endTime: endEpochSeconds
        });
        if (params.length !== 0) {
            url += `?${params.join('&')}`;
        }
        return this._fetchStatForUser(url);
    }
    async _fetchStatForUser(url) {
        const expectedResponses = [200, 404];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, expectedResponses);
        if (response.status === 404) {
            throw new errors_1.UserStatsNotFoundError();
        }
        return response.json;
    }
    async fetchUserContentsStats(ids) {
        return this.fetchStatCollection('/api/stats/contents', ids);
    }
    async fetchUserSectionsStats(ids) {
        return this.fetchStatCollection('/api/stats/sections', ids);
    }
    async fetchUserCoursesStats(ids) {
        return this.fetchStatCollection('/api/stats/courses', ids);
    }
    async fetchStatCollection(path, ids) {
        const saneIds = (0, utils_1.removeDuplicatePrimitives)(ids);
        const requests = [];
        while (saneIds.length > 0) {
            const fullPath = (0, utils_1.buildUrlFromIds)(path, saneIds);
            const request = this.requestMaker.makeRequest(`${this.url}${fullPath}`, {
                method: 'GET'
            });
            requests.push(request.then(response => response.json));
        }
        const responses = await Promise.all(requests);
        return responses.reduce((reducedRes, currentRes) => {
            reducedRes.count += currentRes.count;
            reducedRes.stats.push(...currentRes.stats);
            return reducedRes;
        }, {
            count: 0,
            stats: []
        });
    }
    async fetchWindowedUserCourseStats(userId, startTime, endTime, withStatsGroupedByDateRanges) {
        const expectedResponses = [200, 401, 403];
        let lastKey;
        const responses = [];
        do {
            let url = `${this.url}/api/stats/users/${userId}/windowed/courses`;
            const queryParams = { startTime, endTime };
            if (withStatsGroupedByDateRanges) {
                Object.assign(queryParams, { withStatsGroupedByDateRanges: true });
            }
            const params = this._generateWindowQueryParams(queryParams);
            if (lastKey) {
                params.push(`lastKey=${encodeURIComponent(lastKey)}`);
            }
            if (params.length !== 0) {
                url += `?${params.join('&')}`;
            }
            const response = await this.requestMaker.makeRequest(url, {
                method: 'GET'
            }, expectedResponses);
            if (response.status === 403) {
                throw new errors_1.ForbiddenStatsAccessError();
            }
            if (response.status === 401) {
                throw new errors_1.UnauthorisedStatsAccessError();
            }
            if (response.status === 200) {
                lastKey = response.json.lastKey;
                responses.push(response.json);
            }
        } while (lastKey);
        const responseStartTime = responses.length ? responses[0].startTime : null;
        const responseEndTime = responses.length ? responses[0].endTime : null;
        const groupedStatsByDateRanges = responses.length
            ? responses[0].groupedStatsByDateRanges
            : null;
        const stats = responses.reduce((acc, response) => (Object.assign(Object.assign({}, acc), response.stats)), {});
        return StatsServiceClient._parseWindowedMultiCourseStats({
            userId,
            startTime: responseStartTime,
            endTime: responseEndTime,
            stats,
            groupedStatsByDateRanges
        });
    }
    async fetchWindowedMultiUserCourseStats({ contextId, userIds, courseId, startTime, endTime }) {
        const expectedResponses = [200, 401];
        let url = `${this.url}/api/stats/users/windowed/courses/${courseId}`;
        const params = this._generateWindowQueryParams({
            startTime,
            endTime
        });
        if (params.length !== 0) {
            url += `?${params.join('&')}`;
        }
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ userIds, contextId })
        }, expectedResponses);
        if (response.status === 401) {
            throw new errors_1.UnauthorisedStatsAccessError();
        }
        return StatsServiceClient._parseWindowedMultiUserCourseStats(response.json);
    }
    async adminFetchAggregatedUserSectionStats({ courseId, sectionIds, userIds, bestScoreThreshold }) {
        const expectedResponses = [200, 400];
        const url = `${this.url}/api/admin/stats/sections/aggregated`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({
                courseId,
                sectionIds,
                userIds,
                bestScoreThreshold
            })
        }, expectedResponses);
        if (response.status === 400) {
            const body = response.json;
            throw new errors_1.BadRequestError(body.pointer, body.reason);
        }
        return response.json;
    }
    async fetchAggregatedEnrichedUserSectionStats({ userIds, sectionId, courseId, schoolId }) {
        const expectedResponses = [200, 400, 403];
        const url = `${this.url}/api/stats/sections/${sectionId}/enriched`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ userIds, courseId, schoolId })
        }, expectedResponses);
        if (response.status === 400) {
            const body = response.json;
            throw new errors_1.BadRequestError(body.pointer, body.reason);
        }
        if (response.status === 403) {
            throw new errors_1.ForbiddenStatsAccessError();
        }
        return response.json;
    }
    static _parseWindowedMultiCourseStats({ stats, userId, startTime, endTime, groupedStatsByDateRanges }) {
        const parsedResponse = {
            userId
        };
        if (startTime) {
            parsedResponse.startTime = (0, session_aggregation_1.dateToEpochSeconds)(new Date(startTime));
        }
        if (endTime) {
            parsedResponse.endTime = (0, session_aggregation_1.dateToEpochSeconds)(new Date(endTime));
        }
        if (stats) {
            const parsedResponseStats = Object.entries(stats).reduce((acc, [courseId, userStats]) => {
                return Object.assign(Object.assign({}, acc), { [courseId]: (0, utils_1.parseUserStats)(userStats) });
            }, {});
            parsedResponse.stats = parsedResponseStats;
        }
        if (groupedStatsByDateRanges) {
            parsedResponse.groupedStatsByDateRanges = groupedStatsByDateRanges;
        }
        return parsedResponse;
    }
    static _parseWindowedMultiUserCourseStats(response) {
        const parsedResponse = {
            courseId: response.courseId,
            userIds: response.userIds
        };
        if (response.startTime) {
            parsedResponse.startTime = (0, session_aggregation_1.dateToEpochSeconds)(new Date(response.startTime));
        }
        if (response.endTime) {
            parsedResponse.endTime = (0, session_aggregation_1.dateToEpochSeconds)(new Date(response.endTime));
        }
        if (response.stats) {
            const parsedResponseStats = Object.entries(response.stats).reduce((acc, [userId, userStats]) => {
                const parsedUserStats = Object.assign({ userId }, (0, utils_1.parseUserStats)(userStats));
                if (userStats.earliestStudySession) {
                    parsedUserStats.earliestStudySession = (0, session_aggregation_1.dateToEpochSeconds)(new Date(userStats.earliestStudySession));
                }
                if (userStats.latestStudySession) {
                    parsedUserStats.latestStudySession = (0, session_aggregation_1.dateToEpochSeconds)(new Date(userStats.latestStudySession));
                }
                return Object.assign(Object.assign({}, acc), { [userId]: parsedUserStats });
            }, {});
            parsedResponse.stats = parsedResponseStats;
        }
        return parsedResponse;
    }
    _generateWindowQueryParams({ startTime, endTime, withStatsGroupedByDateRanges }) {
        const params = [];
        !!startTime && params.push(`startTime=${startTime}`);
        !!endTime && params.push(`endTime=${endTime}`);
        !!withStatsGroupedByDateRanges &&
            params.push(`withStatsGroupedByDateRanges=true`);
        return params;
    }
}
exports.default = StatsServiceClient;
//# sourceMappingURL=StatsServiceClient.js.map